import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, WorkoutSession, ExercisePerformance, Exercise, TrainingDay } from '../database';
import { getProgressionPreview } from '../utils/progression';

const WorkoutSummary: React.FC = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [performances, setPerformances] = useState<(ExercisePerformance & { exercise: Exercise })[]>([]);
  const [trainingDay, setTrainingDay] = useState<TrainingDay | null>(null);
  const [progressionPreview, setProgressionPreview] = useState<any[]>([]);

  const loadWorkoutSummary = useCallback(async () => {
    try {
      const sessionData = await db.workoutSessions.get(parseInt(sessionId!));
      if (!sessionData) {
        navigate('/');
        return;
      }

      setSession(sessionData);

      // Load training day
      const trainingDayData = await db.trainingDays.get(sessionData.trainingDayId);
      setTrainingDay(trainingDayData || null);

      // Load performances with exercise details
      const performanceData = await db.exercisePerformances
        .where('sessionId')
        .equals(sessionData.id!)
        .sortBy('order');

      const performancesWithExercises = await Promise.all(
        performanceData.map(async (perf) => {
          const exercise = await db.exercises.get(perf.exerciseId);
          return { ...perf, exercise: exercise! };
        })
      );

      setPerformances(performancesWithExercises);

      // Get progression preview
      const preview = getProgressionPreview(performanceData);
      setProgressionPreview(preview);

      // Get updated weights from training day exercises
      const updatedTrainingDayExercises = await db.trainingDayExercises
        .where('trainingDayId')
        .equals(sessionData.trainingDayId)
        .toArray();

      const weightsMap: {[exerciseId: number]: number} = {};
      updatedTrainingDayExercises.forEach(tde => {
        weightsMap[tde.exerciseId] = tde.weight;
      });

    } catch (error) {
      console.error('Failed to load workout summary:', error);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (sessionId) {
      loadWorkoutSummary();
    }
  }, [sessionId, loadWorkoutSummary]);

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const completedExercises = performances.filter(p => !p.skipped);
  const skippedExercises = performances.filter(p => p.skipped);

  if (!session || !trainingDay) {
    return (
      <div className="text-center py-8">
        <p>Workout data not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-4xl mb-2">🎉</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Workout Complete!
        </h1>
        <h2 className="text-lg text-gray-600 mb-1">
          {trainingDay.name}
        </h2>
        <p className="text-sm text-gray-500">
          {session.startTime.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {completedExercises.length}
          </div>
          <div className="text-sm text-gray-600">
            Exercises
          </div>
        </div>
        
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {session.duration ? formatDuration(session.duration) : 'N/A'}
          </div>
          <div className="text-sm text-gray-600">
            Duration
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {performances.reduce((total, p) => total + (p.targetSets?.length || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">
            Total Sets
          </div>
        </div>
      </div>

      {/* Exercise Summary */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900">Exercise Summary</h3>
        
        {completedExercises.map((performance, index) => {
          const finalSetTarget = performance.targetSets?.[performance.targetSets.length - 1]?.targetReps || 0;
          const hitTarget = performance.finalSetActualReps >= finalSetTarget;
          
          return (
            <button
              key={performance.id}
              onClick={() => navigate(`/workout-edit/${sessionId}/${index}`)}
              className="w-full bg-white border rounded-lg p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {performance.exercise.name}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {performance.weight} lbs × {performance.targetSets?.length || 0} sets
                  </p>
                </div>
                
                <div className="text-right">
                  <div className={`text-lg font-semibold ${hitTarget ? 'text-green-600' : 'text-orange-600'}`}>
                    {performance.finalSetActualReps} reps
                  </div>
                  <div className="text-xs text-gray-500">
                    Target: {finalSetTarget}
                  </div>
                  {hitTarget && (
                    <div className="text-xs text-green-600 font-medium">
                      ✓ Target hit!
                    </div>
                  )}
                </div>
              </div>
              
              {performance.notes && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                  <strong>Notes:</strong> {performance.notes}
                </div>
              )}
            </button>
          );
        })}

        {/* Skipped Exercises */}
        {skippedExercises.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">Skipped Exercises</h4>
            {skippedExercises.map((performance) => (
              <div key={performance.id} className="bg-gray-50 border rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">{performance.exercise.name}</span>
                  <span className="text-sm text-gray-500">Skipped</span>
                </div>
                {performance.notes && (
                  <div className="mt-1 text-sm text-gray-600">
                    {performance.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Progression Preview */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900">Next Workout Weights</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="space-y-3">
            {progressionPreview.map((prog) => {
              const exercise = performances.find(p => p.exerciseId === prog.exerciseId)?.exercise;
              const hasIncrease = prog.adjustment > 0;
              
              return (
                <div key={prog.exerciseId} className="flex justify-between items-center">
                  <div>
                    <span className="font-medium text-gray-900">{exercise?.name}</span>
                    <div className="text-xs text-gray-600">{prog.reason}</div>
                  </div>
                  <div className="text-right">
                    {hasIncrease ? (
                      <div>
                        <span className="text-gray-500 line-through text-sm">{prog.currentWeight} lbs</span>
                        <span className="ml-2 text-green-600 font-semibold">{prog.newWeight} lbs</span>
                        <div className="text-xs text-green-600">+{prog.adjustment} lbs</div>
                      </div>
                    ) : (
                      <span className="text-gray-700 font-medium">{prog.currentWeight} lbs</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <div className="flex space-x-3 relative z-10">
          <button
            onClick={() => navigate('/')}
            className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium cursor-pointer hover:bg-blue-700"
            style={{ pointerEvents: 'auto' }}
          >
            Back to Workouts
          </button>
        </div>
        
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            Tap any exercise above to edit your performance
          </p>
        </div>
        
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Great work! Your progress has been saved.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Next workout: Check your routine for weight adjustments
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkoutSummary;